import { useCallback, useEffect, useState } from "react";
import { supabase } from "./supabase";
import { readCache, writeCache, cacheFresh } from "./useCachedQuery";

// useKidProfiles — list every kid in the signed-in parent's family
// graph. A "kid" is a row in public.profiles with profile_role='child'
// that the parent (or another family adult) is linked to via the
// existing relationships table (kind='family', status='accepted').
//
// We don't query relationships directly — RLS already exposes
// connected profiles via the family_ids_of() helper. Selecting from
// profiles with profile_role='child' inherits the family scope by
// virtue of the existing profiles SELECT policy.
//
// 12h cache per CLAUDE.md egress discipline. Kid roster changes
// rarely (parent adds a kid maybe once per year); 12h TTL means a
// fresh kid landing on another family member's app shows up within
// the day at worst, instantly via realtime if the addition happened
// on a connected device.

const CACHE_KEY = (uid) => `kid-profiles.v1:${uid}`;
const TTL_MS    = 12 * 60 * 60 * 1000;

export function useKidProfiles(userId) {
  const cached = userId ? readCache(CACHE_KEY(userId), TTL_MS) : null;
  const [kids, setKids] = useState(() => cached ?? []);
  const [loading, setLoading] = useState(() => userId ? cached === null : false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!userId) {
      setKids([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    // family_ids_of() already drives RLS on profiles.select for
    // connected users. Filtering by profile_role gets us just the
    // kid rows.
    const { data, error } = await supabase
      .from("profiles")
      .select("id,name,avatar_slug,avatar_url,birth_year,profile_role")
      .eq("profile_role", "child");
    if (error) {
      setError(error);
      console.error("[kid-profiles] load:", error);
    }
    const list = (data || []).filter(p => p.id !== userId);
    setKids(list);
    writeCache(CACHE_KEY(userId), list);
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }
    if (cacheFresh(CACHE_KEY(userId), TTL_MS)) {
      setLoading(false);
      return;
    }
    load();
  }, [userId, load]);

  return { kids, loading, error, refresh: load };
}
